import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaPlus } from "react-icons/fa";

// Component Imports (Paths based on your snippet)
import { Table } from "../../../../../../Components/FinalTable";
import usePouchPagination from "../../../../../../Hooks/usePouchPagination";
import TBody from "./TBody";
import THead from "./THeader";
import BtnPlain from "../../../../../../Components/Buttons/BtnPlain";
import TableContext from "./TContext";

/**
 * 1. Error Boundary Class
 * Catches errors in the component tree, logs them, and renders nothing (null).
 */
class SilentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows nothing
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to the console as requested
    console.error("InvestigationTable Crash Report:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Hide the component entirely
      return null;
    }
    return this.props.children;
  }
}

/**
 * 2. Main InvestigationTable Component
 */
const InvestigationTable = ({ diagnosis, setDiagnosis, handleUpdate }) => {
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState([]);
  const { id } = useParams();
  const [showPagination, setShowPagination] = useState(true);
  const [vpic, setvPic] = useState(null);
  const navigate = useNavigate();
  const [tableIsLoading, setTableIsLoading] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [fixed, setFixed] = useState({});
  const db_name = "visit_investigation";

  const initialIndex = ["created_at"];
  const initialSort = [{ created_at: "desc" }];
  const initialSelector = {
    created_at: { $gte: null },
  };

  // Pagination Logic
  const {
    hasNextPage,
    hasPrevPage,
    handleNext,
    handlePrev,
    handleHeaderQuery,
    handleFilterQuery,
    resetQuery,
    page,
    total,
  } = usePouchPagination({
    db_name,
    limit,
    initialSort,
    initialIndex,
    initialSelector,
    setData,
    setTableIsLoading,
    fixed,
  });

  async function handleClearFilters() {
    await resetQuery();
    setIsFiltered(false);
    setSearchInput("");
  }

  return (
    <TableContext.Provider
      value={{
        limit,
        setLimit,
        data,
        diagnosis,
        setDiagnosis,
        handleUpdate,
        setData,
        vpic,
        setvPic,
        page,
        hasNextPage,
        hasPrevPage,
        handleNext,
        handlePrev,
        handleHeaderQuery,
        handleFilterQuery,
        isFiltered,
        handleClearFilters,
        total,
        setIsFiltered,
        showPagination,
        setFixed,
        resetQuery,
      }}
    >
      <div className="flex flex-col items-start bg-white p-6 rounded-lg mt-6 mb-4">
        <div className="flex flex-row justify-between w-full">
          <p className="font-DM-Med text-lg uppercase text-BLACK">Diagnosis</p>
          <div className="">
            <BtnPlain
              bg="primary"
              textColor="white"
              activeBgColor="secondary"
              mt={2}
              click={() => navigate("add/diagnosis")}
              className="btn"
            >
              <span className="px-2 font-light text-xl">
                <FaPlus />
              </span>
              <span className=" font-pop-reg"> Add Diagnosis </span>
            </BtnPlain>
          </div>
        </div>
        <Table>
          <THead />
          <TBody />
        </Table>
      </div>
    </TableContext.Provider>
  );
};

/**
 * 3. Final Wrapped Export
 * This ensures the boundary covers the Hook calls and the Provider logic.
 */
const SafeInvestigationTable = (props) => (
  <SilentErrorBoundary>
    <InvestigationTable {...props} />
  </SilentErrorBoundary>
);

export default SafeInvestigationTable;
